import { DocumentsPlaceholders } from '@Components/Documents';
import { Permission } from '@Constants/Permission.g';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { useHasPermission } from '@Contexts/appContext';
import type { EnumPaymentMethod, ApiPaymentTerms } from '@Model';
import { paymentMethodOptions, EnumPlaceholderKinds } from '@Model';

import {
    Card,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    TextInput,
    type Nullable,
    Select,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useTranslate,
    NumberInput,
} from '@synqo/frontend-component-library';
import { useParams } from 'react-router';

export function FrameEditPaymentTerms(props: IFrameEditShellProps<ApiPaymentTerms>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiPaymentTerms>
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            apiResourcePath={`${STAMMDATEN_API}/payment_terms`}
            renderer={FrameEditPaymentTermsBody}
            canEdit={props.canEdit !== false && hasPermission(Permission.StammdatenPaymentTermsUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.StammdatenPaymentTermsCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.StammdatenPaymentTermsDelete)}
            frameProps={{ events: { ...frameEditBaseNavigationEvents, ...props.events } }}
        />
    );
}

function FrameEditPaymentTermsBody(props: IFrameBodyProps<ApiPaymentTerms>) {
    const i18n = useTranslate('');

    const binder = useGuiBinder<ApiPaymentTerms>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Zahlungsbedingungen', props.mode),
                resourceLabel: 'Zahlungsbedingungen',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="headdata" border={'withoutBorder'}>
                <Grid>
                    <GridColumns spans={[6, 6]}>
                        <TextInput {...binder.bindToProp<string>('label')} label={i18n('Bezeichnung')} />
                        <TextInput {...binder.bindToProp<string>('shorthand')} label={i18n('Suchcode')} />
                    </GridColumns>
                    <GridColumns spans={[4, 4, 4]}>
                        <TextInput {...binder.bindToProp<string>('booking_code')} label="Buchungscode" />
                        <Select
                            items={paymentMethodOptions}
                            {...binder.bindToAutoConfirmed<Nullable<EnumPaymentMethod>>('payment_method')}
                            label={i18n('Zahlungsart')}
                            clearable
                        />
                        <NumberInput {...binder.bindToProp<number>('due_days')} label={i18n('Fälligkeit (Tage)')} />
                    </GridColumns>
                    <GridColumn span={12}>
                        <TextInput {...binder.bindToProp<string>('remarks')} label={i18n('Bemerkung')} inputSize={3} />
                    </GridColumn>
                    <GridColumns spans={[8, 4]}>
                        <TextInput
                            {...binder.bindToProp<string>('document_representation_template')}
                            label={i18n('Zahlungstext')}
                            inputSize={3}
                        />
                        <DocumentsPlaceholders
                            placeholdersKind={EnumPlaceholderKinds.PaymentTermsDocumentRepresentationTemplate}
                        />
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="Discounts" label="Skonto">
                <Grid>
                    <GridColumns spans={[6, 6]}>
                        <NumberInput {...binder.bindToProp<number>('discount1_days')} label={i18n('Satz 1 (Tage)')} />
                        <NumberInput
                            {...binder.bindToProp<number>('discount1_percentage')}
                            label={i18n('Satz 1 (Prozent)')}
                        />
                    </GridColumns>
                    <GridColumns spans={[6, 6]}>
                        <NumberInput {...binder.bindToProp<number>('discount2_days')} label={i18n('Satz 2 (Tage)')} />
                        <NumberInput
                            {...binder.bindToProp<number>('discount2_percentage')}
                            label={i18n('Satz 2 (Prozent)')}
                        />
                    </GridColumns>
                </Grid>
            </Card>
        </EditFrameShell>
    );
}
